import { Checkbox } from '@consigli/facade';
import { FC } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { FiCalendar, FiTag, FiUser } from 'react-icons/fi';

import { type GroupByOption } from './action-item-types';

interface ActionItemGroupHeaderProps {
  groupKey: string;
  displayName?: string;
  subtitle?: string | null;
  groupBy: GroupByOption;
  itemCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  // Selection props
  isSelected?: boolean;
  isPartiallySelected?: boolean;
  onSelectAll?: () => void;
}

const getGroupIcon = (groupBy: GroupByOption) => {
  switch (groupBy) {
    case 'person':
      return (
        <FiUser
          data-testid="icon-person"
          className="w-4 h-4 text-day-neutral-subtle flex-shrink-0"
        />
      );
    case 'topic':
      return (
        <FiTag data-testid="icon-topic" className="w-4 h-4 text-day-neutral-subtle flex-shrink-0" />
      );
    case 'deadline':
      return (
        <FiCalendar
          data-testid="icon-deadline"
          className="w-4 h-4 text-day-neutral-subtle flex-shrink-0"
        />
      );
    default:
      return null;
  }
};

export const ActionItemGroupHeader: FC<ActionItemGroupHeaderProps> = ({
  groupKey,
  displayName,
  subtitle,
  groupBy,
  itemCount,
  isExpanded,
  onToggle,
  isSelected,
  isPartiallySelected,
  onSelectAll,
}) => {
  // Show checkbox for person groups when selection is available
  const showCheckbox = groupBy === 'person' && !!onSelectAll;

  return (
    <div
      className="flex items-center w-full cursor-pointer hover:bg-day-light-3"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onToggle();
        }
      }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <FaChevronDown
          className={`w-3 h-3 text-day-neutral-subtle transition-transform flex-shrink-0 ${
            isExpanded ? '' : '-rotate-90'
          }`}
        />
        {showCheckbox && (
          <span role="presentation" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              id={`select-all-${groupKey}`}
              checked={isSelected ?? false}
              partiallySelected={isPartiallySelected}
              onChange={() => onSelectAll?.()}
              ariaLabel={`Select all action items for ${displayName ?? groupKey}`}
            />
          </span>
        )}
        {getGroupIcon(groupBy)}
        <div className="flex flex-col items-start">
          <span className="text-sm font-medium text-day-neutral-dark">
            {displayName ?? groupKey}
          </span>
          {subtitle && <span className="text-xs text-day-neutral-subtle">{subtitle}</span>}
        </div>
        <span className="text-xs text-day-neutral-subtle bg-day-light-3 px-2 py-0.5 rounded-full">
          {itemCount}
        </span>
      </div>
    </div>
  );
};
